import { Document, Page, StyleSheet } from '@react-pdf/renderer';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loginPath } from 'src/components/Navbar';
import { RouteLoadErrorMessage } from 'src/components/RouteLoadError';
import { GET_ROUTES } from 'src/graphql/requests/getRoutes';
import { Route } from 'src/graphql/types/delivery';
import {
  GGetRoutesQuery,
  GGetRoutesQueryVariables,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import analytics from 'src/services/analytics';
import { authSelector } from 'src/state/selectors/auth';
import { prepareRoutes } from 'src/state/slices/delivery/getRoutes';
import { clearRouteQuantities } from 'src/state/slices/fulfillmentDeltas';
import { Flex } from 'theme-ui';
import { useQuery } from 'urql';
import FulfillmentTable from '../../components/FulfillmentDeltas/FulfillmentTable';
import { pageContainer } from '../styles';

const FulfillmentDeltasPrintable = (): JSX.Element => {
  const { warehouseId, nightEnd } = useParams();
  const navigate = useNavigate();
  const { isUserAuthenticated } = useAppSelector(authSelector);
  const dispatch = useAppDispatch();
  // Print URLs may differ from the selected delivery context. Keep their
  // requests local so a late response cannot replace the map's route cache.
  const [{ data, error, fetching, operation }, retry] = useQuery<
    GGetRoutesQuery,
    GGetRoutesQueryVariables
  >({
    query: GET_ROUTES,
    variables: {
      input: { warehouseId: warehouseId ?? '', nightEnd: nightEnd ?? '' },
    },
    pause: !warehouseId || !nightEnd,
    requestPolicy: 'network-only',
  });
  const matchesUrl =
    operation?.variables?.input.warehouseId === warehouseId &&
    operation?.variables?.input.nightEnd === nightEnd;
  const routes = useMemo(
    () => (data?.routes ? prepareRoutes(data.routes as Route[]) : undefined),
    [data],
  );
  const matchingRoutes = matchesUrl ? routes : undefined;

  useEffect(() => {
    analytics.track(`"Issues" Page Visit`);
  }, []);

  useEffect(() => {
    if (!isUserAuthenticated) {
      navigate(loginPath);
    }
  }, [isUserAuthenticated, navigate]);

  useEffect(() => {
    dispatch(clearRouteQuantities());
  }, [dispatch, warehouseId, nightEnd]);

  if (matchesUrl && !fetching && (error || !routes))
    return (
      <RouteLoadErrorMessage
        onRetry={(): void => {
          retry({ requestPolicy: 'network-only' });
        }}
      />
    );

  return (
    <Document>
      <Page size="LETTER" style={styles.page} id="page">
        <Flex id="fulfillmentDeltas" sx={pageContainer}>
          {(fetching || !matchingRoutes) && (
            <Flex sx={{ fontFamily: 'body', justifyContent: 'center' }}>
              Loading...
            </Flex>
          )}
          {matchingRoutes && (
            <FulfillmentTable routes={matchingRoutes} printable />
          )}
        </Flex>
      </Page>
    </Document>
  );
};

// Create styles
const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    marginLeft: '24px',
  },
});

export default FulfillmentDeltasPrintable;
